import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { custodyHoldingsApi, schemesApi } from '@/services/api';
import { CustodyHoldings } from '@/types/client';
import { formatNumber, formatSchemeLabel } from '@/utils/formatters';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { loadCompanyOptions, loadClientOptions, LastWorkingDateFilter } from '@/components/utils/filters';
import { FilterAsyncSelect, FilterOption } from '@/components/ui';
import { useTableState } from '@/hooks/useTableState';
import { useClientApi } from '@/hooks/useClientApi';

const CustodyHoldingsList: React.FC = () => {
  const navigate = useNavigate();
  const { getClientFilter } = useClientApi();


  const loadSchemeOptions = useCallback(async (inputValue: string): Promise<FilterOption[]> => {
    try {
      const response = await schemesApi.getSchemes({
        search: inputValue,
        page_size: 50,
        is_active: true,
        ordering: 'code',
      });
      return response.results.map(s => ({ value: s.id.toString(), label: formatSchemeLabel(s) }));
    } catch {
      return [];
    }
  }, []);

  const [custodyHoldings, setCustodyHoldings] = useState<CustodyHoldings[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [selectedDate, setSelectedDate] = useState('');
  // False until LastWorkingDateFilter delivers the default date; the page
  // shows the loader meanwhile instead of an empty table.
  const [dateInitialised, setDateInitialised] = useState(false);
  const [selectedClient, setSelectedClient] = useState<FilterOption | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<FilterOption | null>(null);
  const [selectedScheme, setSelectedScheme] = useState<FilterOption | null>(null);

  const {
    currentPage, pageSize, ordering, searchQuery, debouncedSearch,
    setTotalCount, onSort, onSearchChange, onPageChange, resetPage,
    totalCount, totalPages, sortField, sortDir,
  } = useTableState({ defaultSortField: 'client_master__client_code', searchDebounceMs: 400 });

  const filterParams = useMemo(() => {
    const clientVal = selectedClient ? parseInt(selectedClient.value) : undefined;
    return {
      date: selectedDate,
      ordering,
      ...getClientFilter(clientVal),
      company: selectedCompany ? parseInt(selectedCompany.value) : undefined,
      client_master__scheme: selectedScheme ? parseInt(selectedScheme.value) : undefined,
      search: debouncedSearch || undefined,
    };
  }, [selectedDate, ordering, selectedClient, selectedCompany, selectedScheme, debouncedSearch, getClientFilter]);

  const load = useCallback(async () => {
    if (!filterParams.date) return;
    setLoading(true);
    setError(null);
    try {
      const response = await custodyHoldingsApi.getCustodyHoldings({
        ...filterParams,
        page: currentPage,
        page_size: pageSize,
      });
      setCustodyHoldings(response.results);
      setTotalCount(response.count);
    } catch (err) {
      setError('Failed to load custody holdings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterParams, currentPage, pageSize, setTotalCount]);

  useEffect(() => {
    load();
  }, [load]);

  const showLoader = loading || (!error && !dateInitialised);

  const columns = useMemo((): ColumnDef<CustodyHoldings>[] => [
    { key: 'client_master__client_code', header: 'Client Code', sortable: true, render: r => r.client_code },
    { key: 'client_name', header: 'Client Name', render: r => r.client_name },
    { key: 'company__symbol', header: 'Symbol', sortable: true, render: r => r.company_symbol },
    { key: 'company_name', header: 'Company Name', render: r => r.company_name },
    { key: 'adj_saleable_qty', header: 'Saleable Qty', align: 'right', sortable: true, render: r => formatNumber(r.adj_saleable_qty || 0, 0) },
    { key: 'adj_logical_qty', header: 'Logical Qty', align: 'right', sortable: true, render: r => formatNumber(r.adj_logical_qty || 0, 0) },
  ], []);

  const hasActiveFilters = !!(selectedClient || selectedCompany || selectedScheme || searchQuery.trim());

  const handleClearFilters = () => {
    setSelectedClient(null);
    setSelectedCompany(null);
    setSelectedScheme(null);
    onSearchChange('');
    resetPage();
  };

  const handleDownloadCSV = useCallback(async () => {
    if (!filterParams.date) return;
    setIsDownloading(true);
    try {
      const response = await custodyHoldingsApi.exportAllCustodyHoldings(filterParams);

      const headers = ['Client Code', 'Client Name', 'Symbol', 'Company Name', 'Saleable Qty', 'Logical Qty'];
      const rows = response.results.map(r =>
        [r.client_code, r.client_name, r.company_symbol, r.company_name, r.adj_saleable_qty ?? 0, r.adj_logical_qty ?? 0]
          .map(v => `"${String(v).replace(/"/g, '""')}"`).join(','),
      );

      const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `custody_holdings_${filterParams.date}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error('CSV download failed:', err);
      toast.error('Failed to download CSV. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [filterParams]);

  return (
    <DataTable
      title="Custody Holdings"
      subtitle="Positions as held at the custodian, per client and symbol."
      data={custodyHoldings}
      columns={columns}
      rowKey={r => r.id}
      totalCount={totalCount}
      loading={showLoader}
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      pageSize={pageSize}
      sortField={sortField}
      sortDir={sortDir}
      onSort={onSort}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search by client code, name, symbol..."
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      onDownloadCSV={handleDownloadCSV}
      isDownloading={isDownloading}
      onRowClick={r => navigate(`/custody-holdings/client/${r.client_master}?date=${selectedDate}`)}
      filterSlot={
        <>
          <LastWorkingDateFilter
            value={selectedDate}
            onChange={date => { setSelectedDate(date); setDateInitialised(true); resetPage(); }}
          />

          <FilterAsyncSelect
            label="Scheme"
            value={selectedScheme}
            onChange={opt => { setSelectedScheme(opt); resetPage(); }}
            loadOptions={loadSchemeOptions}
            placeholder="All Schemes"
          />

          <FilterAsyncSelect
            label="Client"
            value={selectedClient}
            onChange={opt => { setSelectedClient(opt); resetPage(); }}
            loadOptions={loadClientOptions}
            placeholder="All Clients"
          />

          <FilterAsyncSelect
            label="Company"
            value={selectedCompany}
            onChange={opt => { setSelectedCompany(opt); resetPage(); }}
            loadOptions={loadCompanyOptions}
            placeholder="All Companies"
          />
        </>
      }
    />
  );
};

export default CustodyHoldingsList;